"use client";

import { useEffect, useRef } from "react";
import { motion } from "framer-motion";

export default function ManifestoSection() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) e.target.classList.add("visible");
        });
      },
      { threshold: 0.1 }
    );
    ref?.current?.querySelectorAll(".reveal")?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <motion.section
      ref={ref}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
      className="py-32 bg-[#11161D] relative overflow-hidden"
    >
      {/* Subtle grid */}
      <div
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage:
            "linear-gradient(rgba(0,230,118,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(0,230,118,0.03) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
        }}
      />
      <div className="relative z-10 max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-20 items-center">
          {/* Left */}
          <div>
            <div className="tag-badge mb-6 reveal">Manifesto</div>
            <h2 className="section-title text-[#F0F4F8] mb-8 reveal reveal-delay-1">
              Infrastructure Should Never Be{" "}
              <span className="text-[#00E676]">The Bottleneck.</span>
            </h2>
            <div className="space-y-5 reveal reveal-delay-2">
              {[
                "We believe scaling brands deserve scalable systems.",
                "We believe ad performance should not pause because infrastructure fails.",
                "We believe growth requires engineering — not restriction.",
              ]?.map((line) => (
                <div key={line} className="flex items-start gap-3">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#00E676] flex-shrink-0" />
                  <p className="text-[#6B7A8D] text-lg leading-relaxed">{line}</p>
                </div>
              ))}
            </div>
            <p className="text-[#F0F4F8] text-base mt-8 font-medium leading-relaxed reveal reveal-delay-3">
              Scaled-Out operates as the backend layer behind serious advertisers.
            </p>
          </div>

          {/* Right — Problem */}
          <div className="reveal reveal-delay-2">
            <div className="tag-badge mb-6">The Problem</div>
            <h3 className="text-2xl font-bold text-[#F0F4F8] mb-8">
              Your Ads Don&apos;t Fail.{" "}
              <span className="text-[#00E676]">Infrastructure Does.</span>
            </h3>
            <div className="space-y-3 mb-10">
              {[
                "Spending caps",
                "Account instability",
                "Slow capital deployment",
                "Overpriced retainers",
                "Delayed support",
              ]?.map((problem) => (
                <div
                  key={problem}
                  className="flex items-center gap-3 p-4 bg-[#0B0F14] border border-[rgba(255,255,255,0.04)] rounded-xl"
                >
                  <span className="text-[#00E676] font-mono text-xs">×</span>
                  <span className="text-[#6B7A8D] text-sm font-medium">{problem}</span>
                </div>
              ))}
            </div>
            <div
              className="p-5 rounded-xl border border-[rgba(0,230,118,0.2)] bg-[rgba(0,230,118,0.04)]"
            >
              <p className="text-[#F0F4F8] text-sm font-semibold">
                Most providers monetize restriction.{" "}
                <span className="text-[#00E676]">We monetize scale.</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </motion.section>
  );
}